<script setup lang="ts">
import { watch, ref, withDefaults } from 'vue';
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'

export type Option = {
  label: string;
  value: any; 
  disabled?: boolean;
  icon?: any;
};

const props = withDefaults(defineProps<{
  label: string;
  description?: string;
  options: Array<Option>;
  selectedOption?: number;
  selectCheckPosition?: "left" | "right";
  block?: boolean; /** Options will take a block, instead of hovering, usefull for small spaces like modals */
}>(), {
  selectCheckPosition: "left",
  block: false
});

const emits = defineEmits<{
  (e: 'update:modelValue', selected: Option): void
}>();

const selected = ref(props.options[props.selectedOption || 0]);

watch(selected, (newSelected) => {
  emits("update:modelValue", newSelected)
});
</script>
<template>
  <Listbox as="div" v-model="selected" :defaultValue="props.options[selectedOption || 0]">
    <ListboxLabel class="block text-sm font-medium leading-6 text-gray-900">{{ label }}</ListboxLabel>
    <p v-if="description" class="text-sm text-slate-600">{{ description }}</p>
    <div class="relative mt-2">
      <ListboxButton class="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6">
        <span class="inline-flex items-center truncate">
          <span class="w-6 px-1 mr-1" v-if="selected.icon"><component :is="selected.icon" :size="16" color="#4b5563" /></span>
          {{ selected.label }}
        </span>
        <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <ChevronUpDownIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
        </span>
      </ListboxButton>

      <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <ListboxOptions
          :class="block
            ? 'relative z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'
            : 'absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'
          "
        >
          <ListboxOption :disabled="Boolean(option.disabled)" as="template" v-for="(option, idx) in options" :key="idx" :value="option" v-slot="{ active, selected, disabled }">
            <li v-if="!disabled" :class="[active ? 'bg-blue-600 text-white' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-8 pr-4']">
              <span class="inline-flex items-center" :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate' ]">
                <span class="w-7" v-if="option.icon"><component :is="option.icon" :size="16" :color="active ? 'white' : '#4b5563'" /></span>
                {{ option.label }}
              </span>
              <span v-if="selected" :class="[active ? 'text-white' : 'text-blue-600', 'absolute inset-y-0  flex items-center',
                selectCheckPosition === 'left' ? 'left-0 pl-1.5' : 'right-2 pr-1.5']">
                <CheckIcon class="w-5 h-5" aria-hidden="true" />
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
</template>